import { FileText, AlertCircle, ShoppingCart, Code, Scale, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

export const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      {/* Header */}
      <section className="hero-gradient py-16 md:py-24" data-testid="terms-header">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#10B981]/10 rounded-full mb-6">
            <FileText size={32} className="text-[#10B981]" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="terms-heading">
            Terms of Service
          </h1>
          <p className="text-lg text-slate-600" data-testid="terms-updated">
            Last updated: December 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24" data-testid="terms-content">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-slate max-w-none">

            {/* Agreement */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Agreement to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                By accessing or using the Snappadeal website, purchasing TRENDO products, or engaging our software consulting services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </div>

            {/* Services Description */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Code size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Our Services</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                Snappadeal provides the following services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li><strong>TRENDO Brand:</strong> E-commerce products sold through Amazon and other platforms</li>
                <li><strong>Software Development:</strong> Custom software solutions and application development</li>
                <li><strong>Software Testing:</strong> Quality assurance, testing services, and consulting</li>
                <li><strong>IT Consulting:</strong> Technology strategy and implementation guidance</li>
              </ul>
            </div>

            {/* E-Commerce Terms */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <ShoppingCart size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">E-Commerce (TRENDO Products)</h2>
              </div>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  <strong>Amazon Purchases:</strong> Products purchased through Amazon are subject to Amazon's terms of service and return policies. We honor Amazon's A-to-z Guarantee for all TRENDO products.
                </p>
                <p className="leading-relaxed">
                  <strong>Product Descriptions:</strong> We strive to provide accurate product descriptions and images. However, slight variations may occur. If you are unsatisfied with a purchase, please contact us or use Amazon's return process.
                </p>
                <p className="leading-relaxed">
                  <strong>Pricing:</strong> All prices are subject to change without notice. We are not responsible for typographical errors in pricing.
                </p>
              </div>
            </div>

            {/* Consulting Services */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Software Consulting Services</h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  <strong>Engagement:</strong> All consulting engagements are subject to a separate agreement or statement of work that outlines scope, deliverables, timelines, and fees.
                </p>
                <p className="leading-relaxed">
                  <strong>Intellectual Property:</strong> Unless otherwise agreed in writing, all work product created during a consulting engagement becomes the property of the client upon full payment.
                </p>
                <p className="leading-relaxed">
                  <strong>Confidentiality:</strong> We maintain strict confidentiality regarding all client information and project details.
                </p>
              </div>
            </div>

            {/* User Responsibilities */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">User Responsibilities</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                When using our website or services, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li>Provide accurate and complete information</li>
                <li>Not use our services for any unlawful purpose</li>
                <li>Not attempt to gain unauthorized access to our systems</li>
                <li>Not interfere with or disrupt our services</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Scale size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Limitation of Liability</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                To the fullest extent permitted by law, Snappadeal shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of our services. Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim.
              </p>
            </div>

            {/* Disclaimers */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Disclaimers</h2>
              <p className="text-slate-600 leading-relaxed">
                Our services are provided "as is" without warranties of any kind, either express or implied. We do not warrant that our services will be uninterrupted, error-free, or secure. You use our services at your own risk.
              </p>
            </div>

            {/* Indemnification */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Indemnification</h2>
              <p className="text-slate-600 leading-relaxed">
                You agree to indemnify and hold harmless Snappadeal, its affiliates, officers, directors, employees, and agents from any claims, damages, losses, or expenses arising out of your use of our services or violation of these terms.
              </p>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Governing Law</h2>
              <p className="text-slate-600 leading-relaxed">
                These Terms of Service shall be governed by and construed in accordance with the laws of the jurisdiction in which Snappadeal operates, without regard to its conflict of law provisions.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Changes to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after any changes constitutes acceptance of the new terms.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-slate-50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Mail size={24} className="text-[#10B981]" />
                <h2 className="font-heading text-2xl font-bold text-slate-900">Contact Us</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you have questions about these Terms of Service, please contact us at:
              </p>
              <a 
                href="mailto:contact@snappadeal.com" 
                className="text-[#10B981] hover:text-[#059669] font-medium"
                data-testid="terms-contact-email"
              >
                contact@snappadeal.com
              </a>
            </div>

            {/* Back Link */}
            <div className="mt-12 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center text-[#10B981] hover:text-[#059669] font-medium"
                data-testid="terms-back-link"
              >
                &larr; Back to Home
              </Link>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
